import { Play } from "lucide-react";
import { motion } from "motion/react";
import { useSectionParallax } from "../../../hooks/useSectionParallax";

export function WorkbenchMock() {
  const pages = ["Import", "Explore", "Model", "Validate", "Report"];
  return (
    <div className="rounded-2xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#0A0A0F]/10 dark:border-white/10">
        <span className="text-xs font-medium text-[#0A0A0F] dark:text-white">iViz Workbench</span>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[#1E4FD8] dark:text-[#4A90D9]">Merlin</span>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#5C5C66] dark:text-[#9A9A9A]"><Play size={10} />Run</span>
        </div>
      </div>
      <div className="grid grid-cols-[76px_1fr_1fr] sm:grid-cols-[92px_1fr_1fr] h-64 text-[10px] sm:text-[11px] overflow-x-auto">
        <div className="border-r border-[#0A0A0F]/10 dark:border-white/10 p-2 sm:p-3 space-y-2.5">
          <p className="text-[9px] font-semibold uppercase tracking-widest text-[#5C5C66] dark:text-[#9A9A9A] mb-1">Pages</p>
          {pages.map((p, i) => (
            <div key={p} className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${i === 1 ? "bg-[#1E4FD8] dark:bg-[#4A90D9]" : "bg-[#0A0A0F]/20 dark:bg-white/20"}`} />
              <span className={i === 1 ? "text-[#0A0A0F] dark:text-white font-medium" : "text-[#5C5C66] dark:text-[#9A9A9A]"}>{p}</span>
            </div>
          ))}
        </div>
        <div className="border-r border-[#0A0A0F]/10 dark:border-white/10 p-2 sm:p-3 font-mono text-[9px] sm:text-[10px] text-[#0A0A0F] dark:text-[#E5E5E8] leading-relaxed whitespace-nowrap">
          <p className="text-[9px] font-semibold uppercase tracking-widest text-[#5C5C66] dark:text-[#9A9A9A] mb-2">Workspace</p>
          <p className="text-[#1E4FD8] dark:text-[#4A90D9]">import pandas as pd</p>
          <p className="mt-2 text-[#5C5C66] dark:text-[#9A9A9A]"># cohort exploration</p>
          <p>df.groupby(<span className="text-[#9A7B3F] dark:text-[#E0C088]">"cohort"</span>)</p>
          <p>&nbsp;&nbsp;.response.mean()</p>
        </div>
        <div className="p-2 sm:p-3 min-w-0">
          <p className="text-[9px] font-semibold uppercase tracking-widest text-[#5C5C66] dark:text-[#9A9A9A] mb-2">Output</p>
          <div className="flex items-end gap-1.5 h-20">
            {[40, 65, 50, 80, 60].map((h, i) => (
              <div key={i} className="flex-1 rounded-t bg-[#1E4FD8] dark:bg-[#4A90D9]" style={{ height: `${h}%`, opacity: 0.25 + i * 0.12 }} />
            ))}
          </div>
          <p className="mt-3 text-[#5C5C66] dark:text-[#9A9A9A]">Data · Charts · Log</p>
        </div>
      </div>
    </div>
  );
}

export function NotAnotherNotebook() {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <div ref={ref} className="grid lg:grid-cols-2 gap-14 items-center">
      <motion.div style={{ y: fgY }}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1E4FD8] dark:text-[#4A90D9] mb-3">
          Notebook · Python or R
        </p>
        <h3 className="text-3xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white mb-4">
          Not another notebook.
        </h3>
        <p className="text-base leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A]">
          Traditional notebooks force code, output, charts, and context into one continuously
          growing document. An iViz notebook is a set of tabbed pages, each a block of code you
          run. Figures, interactive Plotly charts, DataFrames, and file previews appear in a
          results panel beside the code, with printed output and errors in a terminal panel.
        </p>
        <div className="mt-6 space-y-1">
          <p className="text-sm font-medium text-[#0A0A0F] dark:text-white">Pages organize work.</p>
          <p className="text-sm font-medium text-[#0A0A0F] dark:text-white">Outputs stay visible.</p>
          <p className="text-sm font-medium text-[#0A0A0F] dark:text-white">Export to Jupyter anytime.</p>
        </div>
      </motion.div>
      <motion.div style={{ y: bgY }}>
        <WorkbenchMock />
      </motion.div>
    </div>
  );
}
